import { Component, Inject, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { UserService } from '../../../services/user.service';
import { MessagingService } from '../../../components/messaging/messaging.service';
import { OwnerContactUpdate } from '../../../model/user.model';
import { ToolbarService } from '../../../services/toolbar.service';
import { EscapeDialogHelper } from '../../../helpers/escape-dialog.helper';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatDivider } from '@angular/material/divider';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { BrowserAutoFillDisabledDirective } from '../../../directives/browser-auto-fill-disabled.directive';
import { PhoneValidator } from '../../../validators/phone.validator';
import { getErrorMessage } from '../../../consts/form-field-errors';

@Component({
  selector: 'app-edit-contact',
  templateUrl: './edit-contact.component.html',
  encapsulation: ViewEncapsulation.None,
  imports: [
    MatDialogTitle,
    MatIconButton,
    MatDialogClose,
    MatIcon,
    MatDivider,
    CdkScrollable,
    MatDialogContent,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    BrowserAutoFillDisabledDirective,
    MatError,
    MatDialogActions,
    MatButton
  ]
})
export class EditContactComponent implements OnInit {
  contactForm: UntypedFormGroup;
  pending = signal<boolean>(false);
  protected readonly getErrorMessage = getErrorMessage;

  constructor(
    public dialogRef: MatDialogRef<EditContactComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: UntypedFormBuilder,
    private userService: UserService,
    public toolbarService: ToolbarService,
    private message: MessagingService,
    private escapeDialogRef: EscapeDialogHelper
  ) {}

  get email() {
    return this.contactForm.get('email');
  }

  get homePhone() {
    return this.contactForm.get('homePhone');
  }

  get mobilePhone() {
    return this.contactForm.get('mobilePhone');
  }

  get workPhone() {
    return this.contactForm.get('workPhone');
  }

  ngOnInit(): void {
    this.contactForm = this.fb.group({
      contactOrLastName: [''],
      email: [
        '',
        [
          Validators.pattern(
            '^([^@\\s\\."\'\\(\\)\\[\\]\\{\\}\\\\/,:;]+\\.)*[^@\\s\\."\'\\(\\)\\[\\]\\{\\}\\\\/,:;]' +
              '+@[^@\\s\\."\'\\(\\)\\[\\]\\{\\}\\\\/,:;]+(\\.[^@\\s\\."\'\\(\\)\\[\\]\\{\\}\\\\/,:;]+)+$'
          )
        ]
      ],
      homePhone: ['', PhoneValidator],
      mobilePhone: ['', PhoneValidator],
      workPhone: ['', PhoneValidator],
      postCode: ['']
    });

    if (!this.data.ownerIsACompany) {
      this.contactForm.controls.contactOrLastName.disable();
    }

    if (this.data.cancelled) {
      this.contactForm.disable();
    }

    this.escapeDialogRef.escapeDialog(this.dialogRef);

    this.homePhone.markAsTouched();
    this.mobilePhone.markAsTouched();
    this.workPhone.markAsTouched();

    this.email.valueChanges.subscribe(() => {
      if (this.email.invalid) {
        this.email.markAsTouched();
      }
    });
  }

  saveContact() {
    this.pending.set(true);
    this.toolbarService.setCloudState('SAVING');

    const ownerContactData: OwnerContactUpdate = {};

    if (this.contactForm.controls.email.dirty) {
      ownerContactData.email = this.contactForm.controls.email.value.trim();
    }

    if (this.contactForm.controls.homePhone.dirty) {
      ownerContactData.homePhone = this.contactForm.controls.homePhone.value.trim();
    }

    if (this.contactForm.controls.mobilePhone.dirty) {
      ownerContactData.mobilePhone = this.contactForm.controls.mobilePhone.value.trim();
    }

    if (this.contactForm.controls.workPhone.dirty) {
      ownerContactData.workPhone = this.contactForm.controls.workPhone.value.trim();
    }

    if (this.contactForm.controls.postCode.dirty) {
      ownerContactData.postCode = this.contactForm.controls.postCode.value.trim();
    }

    if (this.data.ownerIsACompany && this.contactForm.controls.contactOrLastName.dirty) {
      ownerContactData.contact = this.contactForm.controls.contactOrLastName.value.trim();
    }

    this.userService.updateOwnerContact(this.data.jobId, ownerContactData).subscribe(
      () => this.dialogRef.close('Success'),
      () => {
        this.message.error('Could not update contact.');
        this.toolbarService.setCloudState('RESTING');
        this.pending.set(false);
      },
      () => {
        this.toolbarService.setCloudState('RESTING');
        this.pending.set(false);
      }
    );
  }
}
